import "./styles.scss";

import { observer } from "mobx-react";
import React, { useRef, useState } from "react";
import Popup from "reactjs-popup";

import { Icon, IconButton, Typography } from "@/components/Elements";
import Checkbox from "@/components/table/shared/Checkbox";
import { CommentTab } from "@/components/table/shared/comments/CommentsSlideMenu/types";
import { UsersCircle } from "@/components/users-circle";
import { useMainStore } from "@/contexts/Store";
import { useSearchParams } from "@/hooks/useSearchParams";
import { isNotItem } from "@/utils/utils";

export type UsersSearchSelectProps = {
  headerTitle: string;
  selectedUserIDs: number[];
  onClose(userIDs: number[]): void;
};

function UsersSearchSelect(props: UsersSearchSelectProps) {
  const mainStore = useMainStore();
  const [searchInput, setSearchInput] = useState("");

  const [selectedUserIDs, setSelectedUserIDs] = useState(props.selectedUserIDs);

  const dropdownContainerRef = useRef<HTMLDivElement>();
  const [searchParams] = useSearchParams<{ comments_tab?: CommentTab }>();

  const { isCurrentWorkspaceArchived } = mainStore.workspaces;
  const isInternalTab = searchParams.comments_tab === "internal";

  const activeUsers = isInternalTab
    ? mainStore.comments.privateUsers
    : mainStore.users.activeDisplayUsers;

  const userList = [
    ...activeUsers,
    // only show inactive users in list if they are selected
    ...mainStore.users.inactiveDisplayUsers.filter((inactiveUser) =>
      selectedUserIDs.includes(inactiveUser.id),
    ),
  ];

  const filteredUsers = userList.filter((user) =>
    user.display.toLowerCase().includes(searchInput),
  );

  function handleClickUser(userID: number, currentlyChecked: boolean) {
    return setSelectedUserIDs((prevSelectedUsers) =>
      currentlyChecked
        ? prevSelectedUsers.filter(isNotItem(userID))
        : prevSelectedUsers.concat(userID),
    );
  }

  if (isCurrentWorkspaceArchived) {
    return null;
  }

  return (
    <Popup
      keepTooltipInside
      trigger={(open) => (
        <div data-testid="add_assignees">
          <IconButton transparent icon="user" active={open} />
        </div>
      )}
      onClose={() => props.onClose(selectedUserIDs)}
    >
      <div
        role="menu"
        // @ts-expect-error TS(2322) FIXME: Type 'MutableRefObject<HTMLDivElement | undefined>... Remove this comment to see the full error message
        ref={dropdownContainerRef}
        // Keeps width the same as initial even after filtering users with search
        style={{ width: dropdownContainerRef.current?.offsetWidth }}
        className="users-search-select__dropdown-container users-search-select__header-title"
      >
        <Typography
          label={props.headerTitle}
          weight="semiBold"
          color="extrasSlateBlue"
          size="sm"
          style={{ paddingLeft: 16 }}
        />
        <div className="users-search-select__input-container">
          <input
            className="users-search-select__search-input"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search User"
          />
          <Icon name="search" />
        </div>
        <ul className="users-search-select__users-list-container">
          {filteredUsers.map((user) => {
            const checked = selectedUserIDs.includes(user.id);

            return (
              <li
                key={user.id}
                role="menuitem"
                className="users-search-select__user-select-item"
                onClick={() => handleClickUser(user.id, checked)}
              >
                <Checkbox
                  small
                  checked={checked}
                  onClick={(event) => event.stopPropagation()}
                />
                <UsersCircle
                  small
                  iconColorIndex={user.icon_color_index}
                  initials={user.initials}
                />
                <Typography
                  label={user.display}
                  weight="semiBold"
                  color="generalMidnightDark"
                  size="sm"
                />
              </li>
            );
          })}
        </ul>
      </div>
    </Popup>
  );
}

export default observer(UsersSearchSelect);
